<template>
	<div class="c-search" :class="{ 'c-search--active': isFocus || isOpen }">
		<div class="c-search__input">
			<SearchIcon class="search"></SearchIcon>
			<input
				type="text"
				:placeholder="translations['Search goods'] + '...'"
				v-model="queryString"
				@focusin="focus(true)"
				@focusout="focus(false)"
				ref="searchInput"
			/>
			<div class="close" v-if="!loading && queryString.length" @click="clear">
				<CloseIcon></CloseIcon>
			</div>

			<div class="loader" v-if="loading">
				<Loader class="o-loader--blue o-loader--column o-loader--tiny"></Loader>
			</div>
		</div>

		<Transition name="fade">
			<div v-if="isOpen" class="c-search__mega">
				<div class="overlay" @click="close(false)"></div>

				<div class="content">
					<div class="content__header">
						<div class="close" @click="close(true)">
							<CloseIcon></CloseIcon>
						</div>
					</div>

					<!-- RESULTS -->
					<div class="content__results" v-if="productResults || menuResults">
						<div class="menu" v-if="menuResults">
							<div
								class="menu__content"
								v-for="menuItem in menu"
								:key="menuItem.heading"
								v-show="menuItem.show"
							>
								<h3 class="heading">{{ menuItem.heading }}</h3>
								<ul class="items">
									<li
										class="items__item"
										v-for="item in menuItem.items"
										:key="item.id"
									>
										<a :href="item.url">{{ item.title }}</a>
									</li>
								</ul>
							</div>
						</div>

						<div class="products products__results" v-if="productResults">
							<h3>{{ translations["Products"] }}</h3>

							<div class="catalog">
								<SearchProduct
									:product="product"
									v-for="product in products"
									:key="product.id"
								/>
							</div>
						</div>
						<div v-else class="products products__noresults">
							<span>{{ translations["No results"] }}</span>
						</div>
					</div>

					<div
						class="content__all"
						@click="navigateToResults()"
						v-if="productResults || menuResults"
					>
						<span>{{ translations["Show all results"] }}</span>
					</div>

					<div
						class="content__noresults"
						v-if="!productResults && !menuResults"
					>
						<span>{{ translations["No results"] }}</span>
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script>
import axios from "axios";
// ICONS
import SearchIcon from "@web/assets/icon/search.svg";
import CloseIcon from "@web/assets/icon/close.svg";

// COMPONENTS
import Loader from "@/vue/components/objects/Loader.vue";
import SearchProduct from "@/vue/components/product/SearchProduct.vue";

// UTILS
import { debounce } from "@/vue/utils/helpers";
import request from "@/vue/mixins/request.js";
import { site } from "@/vue/mixins/map.ts";

export default {
	name: "Search",
	mixins: [request, site],
	components: {
		SearchIcon,
		CloseIcon,
		Loader,
		SearchProduct,
	},
	data: () => {
		return {
			isOpen: false,
			isFocus: false,
			queryString: "",

			products: [],
			menu: [],

			menuResults: false,
			productResults: false,

			cancelToken: "",
			axiosSource: null,
		};
	},
	watch: {
		isOpen(value) {
			const headerElement = document.querySelector(".p-header");
			if (!headerElement) {
				return;
			}
			value
				? headerElement.classList.add("p-header--searchOpen")
				: headerElement.classList.remove("p-header--searchOpen");
		},

		queryString(query) {
			this.changed(query, this);
		},
	},
	methods: {
		changed: debounce((query, vm) => {
			vm.queryString = query;
			if (vm.cancelToken) {
				vm.axiosSource.cancel("Operation cancelled by the user.");
			}
			vm.search();
		}, 500),

		clear() {
			this.queryString = "";
			this.$refs?.searchInput?.focus();
		},

		focus(value) {
			this.isFocus = value;

			if (value && this.queryString.length > 1) {
				this.open();
			}
		},

		open() {
			document.addEventListener("keydown", this.keyHandler);
			document.getElementById("body")?.classList.add("scroll-lock");
			this.isOpen = true;
		},

		close(clear = false) {
			document.removeEventListener("keydown", this.keyHandler);
			document.getElementById("body")?.classList.remove("scroll-lock");
			this.$refs?.searchInput?.blur();
			this.isOpen = false;

			if (clear) {
				this.queryString = "";
			}
		},

		keyHandler: function (e) {
			if (e.key === "Escape" && this.isOpen) {
				this.close(true);
				return;
			}
		},

		navigateToResults() {
			window.location.href =
				this.siteUrl + "search?query=" + this.queryString.replace(" ", "%20");
		},

		search() {
			//We need at least two characters before making a search
			if (this.queryString.length < 2) {
				this.loading = false;
				return;
			}

			this.loading = true;
			const query = encodeURIComponent(this.queryString);
			// const url = '/api/qd-search/search?query=' + query;
			const url = this.siteUrl + "api/qd/search/v1/search?query=" + query;

			this.axiosSource = axios.CancelToken.source();
			this.cancelToken = this.axiosSource.token;

			axios
				.get(url, {
					cancelToken: this.cancelToken,
				})
				.then((res) => {
					this.open();

					this.products = res.data.result.products;
					this.menu = res.data.result.menus;
					this.menuResults = res.data.result.menuResults;
					this.productResults = res.data.result.productResults;

					this.loading = false;
					this.cancelToken = "";
				});
		},
	},
	mounted() {},
};
</script>

<style lang="scss">
@import "@backend/scss/abstracts/variables.scss";
@import "@backend/scss/abstracts/mixins.scss";

.c-search {
	height: 100%;
	width: 100%;

	&__input {
		transition: $t-hover;
		background-color: $c-white;
		position: relative;
		z-index: 15;
		top: -1px;
		height: calc(100% + 2px);

		padding-left: 25px;
		display: flex;
		justify-content: center;
		align-items: center;

		border: $e-border;

		.search {
			@include size-fix(20px, 20px);
			display: flex;
			justify-content: center;
			align-items: center;
			margin-right: 16px;
		}

		.close {
			cursor: pointer;
			height: 100%;
			padding-right: 25px;
			padding-left: 10px;

			display: flex;
			justify-content: flex-end;
			align-items: center;

			svg {
				width: 12px;
				height: 12px;
			}
		}

		.loader {
			padding-right: 25px;
		}

		input {
			all: unset;

			display: flex;
			flex: 1;

			height: calc(100% - 2px);
			height: 100%;
			font-size: $font-base-size;
			line-height: $font-base-size;

			font-weight: $f-regular;
			color: $c-blue;

			&::placeholder {
				position: relative;
				top: -1px;
				padding-left: 2px;
				line-height: $font-base-size;
				font-size: $font-base-size;
				color: $c-grey-text;
			}
		}
	}

	&__mega {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		z-index: 10;

		.content {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;

			padding-top: 48px;

			z-index: 1;
			background-color: $c-white;

			&__header {
				@include grid-keep();
				height: 64px;
				display: flex;
				align-items: center;
				justify-content: flex-end;

				.close {
					cursor: pointer;
					width: 35px;
					height: 35px;
					display: flex;
					justify-content: flex-end;
					align-items: center;

					svg {
						transition: $t-hover;
						width: 16px;
						height: 16px;
					}

					&:hover {
						svg {
							transition: $t-hover;
							opacity: 0.5;
						}
					}
				}
			}

			&__results {
				@include grid-left();
				gap: 16px;
				padding-top: 48px;

				display: flex;

				.menu {
					width: 20%;
					min-width: 20%;

					&__content {
						.heading {
							font-weight: $f-semibold;
						}

						.items {
							margin-top: 8px;
							margin-bottom: 32px;
							padding-right: 16px;
							&__item {
								@include hover-underline();
								@include text-overflow(1);
								margin-bottom: 8px;
							}
						}
					}
				}

				.products {
					display: flex;
					flex: 1;

					&__results {
						width: 80%;
						max-width: 100%;
						position: relative;

						&::before {
							position: absolute;
							top: 23px;
							z-index: 15;
							right: 11px;
							left: 0;
							height: 20px;
							display: block;
							content: "";
							background: linear-gradient(
								180deg,
								rgba(255, 255, 255, 1) 0%,
								rgba(255, 255, 255, 0) 100%
							);
						}

						flex-direction: column;

						.catalog {
							@include scroll-hidden();
							$paddingLeft: 20px;

							overflow-y: auto;
							max-height: 60vh;
							position: relative;
							// left: -20px;
							gap: 20px 40px;

							padding-bottom: 30px;
							padding-left: $paddingLeft; // Compensate for box shadow on result cards
							padding-top: 10px;
							padding-right: 40px;
							margin-left: $paddingLeft * -1; // Compensate for box shadow on result cards

							width: auto; // Compensate for box shadow on result cards
							max-width: auto; // Compensate for box shadow on result cards
							display: flex;
							flex-wrap: wrap;

							// @media screen and (min-width: 769px) {
							// 	@include scroll-blue();
							// }

							.c-searchProduct {
								width: calc(50% - 20px);
								max-width: unset;

								&:hover {
									z-index: 10;
								}

								&__favorite {
									display: none;
								}

								&__info {
									.button {
										display: none;
									}

									h3 {
										margin: 0;
										text-align: left;
									}

									.p-disclaimer {
										margin-top: 7px;
										font-size: 14px;
										line-height: 16px;
										margin-bottom: 2px;
									}
								}
							}
						}
					}

					&__noresults {
						justify-content: center;
						align-items: center;
					}
				}
			}

			&__all {
				// margin-top: 28px;
				width: 100%;
				height: 76px;
				border-top: $e-border;

				display: flex;
				justify-content: center;
				align-items: center;

				cursor: pointer;

				span {
					position: relative;
					font-weight: $f-semibold;

					&::after {
						content: "";
						position: absolute;
						bottom: 0;
						left: 0px;
						width: 100%;
						height: 1px;
						background-color: $c-blue;
						transition: width 250ms cubic-bezier(0.395, 0.325, 0.365, 0.995);
					}
				}

				&:hover {
					span {
						&::after {
							width: 0%;
							transition: width 250ms cubic-bezier(0.395, 0.325, 0.365, 0.995);
						}
					}
				}
			}

			&__noresults {
				display: flex;
				justify-content: center;

				padding-top: 48px;
				padding-bottom: 48px;

				span {
					font-weight: $f-semibold;
				}
			}
		}

		.overlay {
			position: fixed;
			width: 100%;
			height: 100vh;
			background-color: $c-overlay;
			bottom: 0;
			left: 0;
			z-index: -1;
		}
	}

	&--active {
		.c-search {
			position: relative;
			z-index: 10;

			&__input {
				transition: $t-hover;
				border: $e-border-blue;
			}
		}
	}
}

@media all and (max-width: 960px) {
	.c-search {
		&__mega {
			.content {
				&__results {
					.menu {
						width: 25%;
						min-width: 25%;
					}

					.products {
						&__results {
							width: 75%;
							min-width: 75%;

							.catalog {
								.c-searchProduct {
									width: 100%;
								}
							}
						}
					}
				}
			}
		}
	}
}

@media all and (max-width: 768px) {
	.c-search {
		position: absolute;
		left: 0px;
		transition: $t-hover;
		pointer-events: none;

		&__mega {
			pointer-events: all;
			top: -96px;

			.content {
				height: 100vh;
				padding-top: 32px;

				display: flex;
				flex-direction: column;

				&__header {
					height: 64px;
				}

				&__all {
					margin-top: 0;
				}

				&__results {
					height: calc(100vh - 252px);
					overflow: auto;

					padding-top: 0px;
					margin-top: 55px;
					padding-top: 1px;

					flex-direction: column;
					flex: 1;

					@include scroll-blue();

					.menu {
						width: 100%;
						margin-bottom: 15px;

						&__content {
							display: flex;
							align-items: stretch;
							position: relative;
							border-bottom: 1px solid #dfdfdf;

							&::before {
								position: absolute;
								top: 0;
								z-index: 15;
								left: 100px;
								bottom: 0;
								display: block;
								width: 15px;
								content: "";
								background: linear-gradient(
									90deg,
									rgba(255, 255, 255, 1) 0%,
									rgba(255, 255, 255, 0) 100%
								);
							}

							&::after {
								position: absolute;
								top: 0;
								z-index: 15;
								right: 0px;
								bottom: 0;
								display: block;
								width: 30px;
								content: "";
								background: linear-gradient(
									270deg,
									rgba(255, 255, 255, 1) 0%,
									rgba(255, 255, 255, 0) 100%
								);
							}

							.heading {
								width: 100px;
								min-width: 100px;
								padding-right: 5px;
								border-right: 1px solid #dfdfdf;
								padding-bottom: 9px;
								padding-top: 7px;
							}

							.items {
								margin: 0;
								padding-left: 15px;
								padding-right: 25px;

								display: flex;
								white-space: nowrap;
								align-items: center;
								gap: 16px;

								overflow-x: auto;

								&__item {
									margin-bottom: 0;
									overflow: visible;
									@media (hover: none) {
										&::after {
											display: none;
										}
									}
								}
							}
						}
					}

					.products {
						&__results {
							width: 100%;
							min-width: 100%;

							.catalog {
								padding-left: 0;
								padding-right: 16px;
								padding-bottom: 48px;
								max-height: unset;
								gap: 16px;
								overflow-y: visible;

								//left: 0px;
								width: 100%; // Disable box shadow compensation on mobile
								max-width: 100%; // Disable box shadow compensation on mobile
								margin-left: 0; // Disable box shadow compensation on mobile

								.c-searchProduct {
									min-width: calc((100% / 2) - 10px);
									max-width: calc((100% / 2) - 10px);
								}
							}
						}

						&__noresults {
							display: none;
						}
					}
				}

				&__noresults {
					display: flex;
					justify-content: center;
					margin-top: 57px;

					padding-top: 48px;
					padding-bottom: 48px;

					span {
						font-weight: $f-semibold;
					}
				}
			}
		}

		&__input {
			width: calc(100% - 59px);
			height: 57px;

			left: 59px;
			pointer-events: all;
			padding-left: 16px;

			transition: $t-hover;
			border-right: none;

			.close {
				padding-right: 16px;
			}

			.loader {
				padding-right: 16px;
			}
		}

		&--active {
			transition: $t-hover;
			.c-search {
				&__input {
					left: 0;
					border: $e-border;

					transition: $t-hover;
					width: 100%;

					input {
						height: 100%;
					}
				}
			}
		}
	}
}

@media all and (max-width: 620px) {
	.c-search {
		&__mega {
			.content {
				&__results {
					@include scroll-blue();
					height: calc(100vh - 252px);
					overflow: auto;

					padding-top: 0px;
					margin-top: 55px;
					padding-top: 1px;

					.products {
						&__results {
							.catalog {
								gap: 8px;

								.c-searchProduct {
									width: 100%;
									max-width: unset;
								}
							}
						}
					}
				}

				&__all {
					margin-top: 0;
				}
			}
		}
	}
}

@media all and (max-height: 850px) {
	.c-search {
		&__mega {
			.content {
				height: 100vh;
				display: flex;
				flex-direction: column;
				justify-content: space-between;
				&__results {
					overflow: auto;
					height: 100%;

					.products__results {
						.catalog {
							max-height: 100%;
						}
					}
				}

				&__all {
					margin-top: 0;
				}
			}
		}
	}
}
</style>
